/**
 * Dashboard page with statistics and recent vouchers
 */
import React, { useEffect } from 'react';
import {
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowRight,
  Eye,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useVoucherStore } from '@/store/voucherStore';
import { Loading } from '@/components/Loading';
import { formatCurrency, formatDate, isExpired, isExpiringSoon } from '@/utils/dateUtils';
import { VoucherStatus } from '@/types';

const iconColorMap = {
  primary: 'text-primary',
  success: 'text-success',
  error: 'text-destructive',
  warning: 'text-warning',
  default: 'text-muted-foreground',
} as const;

function StatCard({
  title,
  value,
  subtitle,
  icon,
  color,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: keyof typeof iconColorMap;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="mb-1 text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-semibold">{value}</p>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div className={iconColorMap[color]}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { statistics, vouchers, isLoading, fetchStatistics, fetchVouchers } = useVoucherStore();

  useEffect(() => {
    fetchStatistics();
    fetchVouchers();
  }, [fetchStatistics, fetchVouchers]);

  if (isLoading && !statistics) {
    return <Loading message="Loading dashboard..." />;
  }

  const recentVouchers = vouchers.slice(0, 5);

  const getStatusVariant = (status: VoucherStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case VoucherStatus.ACTIVE:
        return 'default';
      case VoucherStatus.USED:
        return 'secondary';
      case VoucherStatus.EXPIRED:
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getExpiryWarning = (voucher: (typeof vouchers)[0]) => {
    if (isExpired(voucher.expiry_date)) {
      return { variant: 'destructive' as const, label: 'Expired' };
    }
    if (isExpiringSoon(voucher.expiry_date)) {
      return { variant: 'outline' as const, label: 'Expiring Soon' };
    }
    return null;
  };

  return (
    <div>
      <h1 className="mb-4 text-2xl font-semibold">Dashboard</h1>

      {statistics && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          <StatCard
            title="Total Vouchers"
            value={statistics.total_vouchers}
            subtitle={formatCurrency(statistics.total_value)}
            icon={<TrendingUp className="size-10" />}
            color="primary"
          />
          <StatCard
            title="Active"
            value={statistics.active_count}
            subtitle={formatCurrency(statistics.active_value)}
            icon={<CheckCircle className="size-10" />}
            color="success"
          />
          <StatCard
            title="Used"
            value={statistics.used_count}
            subtitle={formatCurrency(statistics.used_value)}
            icon={<XCircle className="size-10" />}
            color="default"
          />
          <StatCard
            title="Expiring Soon"
            value={statistics.expiring_soon}
            subtitle="Next 30 days"
            icon={<AlertTriangle className="size-10" />}
            color="warning"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Recent Vouchers</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/vouchers')}
                className="gap-1"
              >
                View All
                <ArrowRight className="size-4" />
              </Button>
            </div>
            {recentVouchers.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                No vouchers found. Upload your first voucher to get started!
              </p>
            ) : (
              <ul className="space-y-2">
                {recentVouchers.map((voucher) => {
                  const expiryWarning = getExpiryWarning(voucher);
                  return (
                    <li
                      key={voucher.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{voucher.brand_name}</span>
                          <Badge variant={getStatusVariant(voucher.status)}>
                            {voucher.status}
                          </Badge>
                          {expiryWarning && (
                            <Badge variant={expiryWarning.variant}>
                              {expiryWarning.label}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(voucher.value)} • {voucher.source_platform}
                        </p>
                        {voucher.expiry_date && (
                          <p className="text-xs text-muted-foreground">
                            Expires: {formatDate(voucher.expiry_date)}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/vouchers/${voucher.id}`)}
                        aria-label="View details"
                      >
                        <Eye className="size-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold">Quick Filters</h2>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/vouchers?status=active')}
              >
                Active Vouchers
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/vouchers?status=used')}
              >
                Used Vouchers
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/vouchers?status=expired')}
              >
                Expired Vouchers
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/upload')}
              >
                Upload Voucher
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
